import { Button, Classes, Colors, Dialog, Intent, ButtonGroup } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, useGridState, useSearchApi, ButtonContainer } from "nsitools-react";
import * as React from "react";

import { DogTitlesApi, DogTitleSearch, DogTitlesGridDto } from "../../../../../../api";
import { AddButton, DeleteButton, CustomDataTable, ExportButton } from "../../../../../../components";
import { useGlobalData, useEventsContext } from "../../../../../../contexts";
import { useApiService, useTl, useDeleteApi } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { DogResultCard } from "./DogResultsCard";
import { DogTitlesPanelStack } from "./DogTitlesPanelStack";
import { Link } from "react-router-dom";
import { ERoutes } from "../../../../../../ERoutes";

export interface IDogTitlesListProps {
  dogId: number;
  editMode: boolean;
}

export const DogTitlesList: React.FunctionComponent<IDogTitlesListProps> = ({ dogId, editMode }) => {
  const api = useApiService(DogTitlesApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const { dispatchEvent } = useEventsContext();
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5, 10, 15],
    pageSize: 10,
    sortKeys: { year: "DESC", titleType: "ASC", titleName: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogTitleSearch) => {
      setsobjState(DogTitleSearch.fromJS({ ...sObj, dogId: dogId, languageId: currentLanguage.languageId }));
      return api.baseSearch(DogTitleSearch.fromJS({ ...sObj, dogId: dogId, languageId: currentLanguage.languageId }));
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.deleteDogTitle(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );
  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        hidden: !editMode,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DogTitlesGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.dogTitleId)} />
          </ButtonContainer>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "_toggle",
        autoFitContent: true,
        render: (item: DogTitlesGridDto, _, { subPanelOpened, toggleSubPanel }) => (
          <Button icon={subPanelOpened ? "minus" : "plus"} onClick={() => toggleSubPanel()} />
        ),
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "titleName",
      },
      {
        header: () => t(ETLCodes.Country),
        fieldName: "country",
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "titleType",
        render: (row: DogTitlesGridDto) => (
          <Link target="_blank" to={`${ERoutes.titleType}/${row.titleTypeId}/detail/view`}>
            {row.titleType}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Year),
        fieldName: "year",
      },
    ],
    [deleteItem, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Titles)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable
          tableState={tableState}
          loading={loading}
          columns={columns}
          keyFieldName="dogTitleId"
          renderSubComponent={(item: DogTitlesGridDto) => <DogResultCard data={item.competitionResults} />}
        />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.TitleSelection)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1300, height: 500, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <DogTitlesPanelStack
            dogId={dogId}
            onSaved={() => {
              setDialogOpened(false);
              search();
              dispatchEvent("ReloadTitlesTable");
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
